import angular from 'angular';
import { debounce, includes, max, throttle } from 'lodash';

import _css from './od-sneaky-element.less';

const VALID_MODES = [
  'height',
  'fade-in',
  'fade-out'
];

// od-sneaky-element
//
// An element that is hidden until you scroll past a certain point on the page.
//
// That point on the page is configurable and can be relative to some other
// element (specified by an id), or to the page itself.
//
// Once you scroll past this point, the sneaky element will slowly come into
// view. We set the `height` property of <sneaky-element> progressively, and
// use `overflow:hidden`, so that it "animates" into view as you scroll rather
// than appearing all at once.
//
// More information, and a live demo, is available on the style guide.
// http://opendoor.com/style
//
// Usage:
//   <sneaky-element> ... </sneaky-element>
//
// Parameters:
//   relative-element-id: Set to the id (not class) of another element on the
//     page to set the scroll breakpoint relative to the bottom of that
//     element. Lets you show the <sneaky-element> once another element on the
//     page has scrolled out of view.
//   scroll-parent-id: Set to the id (not class) of the element's scroll parent
//     (the outer element within which the scrollbar appears). If left blank,
//     defaults to window; otherwise the top-buffer and relative-element offset
//     will be measured relative to the scroll-parent's current vertical scroll.
//   top-buffer: Adjust the scroll breakpoint up or down. For example, set to
//     40 if you want the sneaky element to come into view when the user has
//     scrolled 40px PAST the relative element or top of page. Set to -40 if
//     you want the sneaky element to come into view when the use has scrolled
//     to 40px above the relative element or top of page.
//   mode: "height"|"fade-in"|"fade-out": what to do when the scroll breakpoint
//     is reached. Use "height" to animate the height from 0 to full height,
//     "fade-in" to fade in using opacity, or "fade-out" to fade out using
//     opacity.
//   stay-open: If set to `true`, then the height of the sneaky element will
//     only increase (so it won't collapse again when the user scrolls back
//     up). Only supports "height" mode.
module.exports = angular.module('common.directives.od-sneaky-element', [])
.directive('odSneakyElement', ($window, $document) => {
  class OdSneakyElement {
    constructor($element, $scope) {
      // Component parameters
      // ----------------------------------------------------------------------
      this.topBuffer = 0;
      this.stayOpen = false;
      this.relativeElementId = null;
      if (!includes(VALID_MODES, this.mode)) {
        this.mode = 'height';
      }

      // Setup
      // ----------------------------------------------------------------------
      this.$element = $element;
      this.currentHeight = null;

      let scrollParent;
      if (this.scrollParentId) {
        let scrollParentEl = $document[0].getElementById(this.scrollParentId);
        scrollParent = angular.element(scrollParentEl);
      } else {
        scrollParent = angular.element($window);
      }
      var throttledOnScroll = throttle(() => {
        this.updateSneakyElement();
      }, 10);
      var debouncedRebuild = debounce(() => {
        this.rebuild();
        this.updateSneakyElement();
      }, 300, { leading: true });

      scrollParent.on('scroll', throttledOnScroll);
      scrollParent.on('scroll', debouncedRebuild);
      scrollParent.on('resize', debouncedRebuild);
      $scope.$on('$destroy', function() {
        scrollParent.off('scroll', throttledOnScroll);
        scrollParent.off('scroll', debouncedRebuild);
        scrollParent.off('resize', debouncedRebuild);
      });
      this.scrollParent = scrollParent;

      // Will be updated by rebuild
      this.scrollBreakpoint = null;
      this.innerHeight = null;
      this.rebuild();

      this.updateSneakyElement();
    }

    // We don't want to recompute the sneakyElement inner height, or the
    // position of the relative element, very often (because those operations
    // are expensive). So we do it in a rebuild function and debounce it.
    rebuild() {
      this.scrollBreakpoint = this.getScrollBreakpoint();
      this.innerHeight = this.getInnerHeight();
    }

    getCurrentScroll() {
      return (this.scrollParentId) ? this.scrollParent.scrollTop() : $window.scrollY;
    }

    getElementHeight(el) {
      return parseInt($window.getComputedStyle(el).height);
    }

    getInnerHeight() {
      // We can rely on having one child because the directive uses
      // transclusion to wrap its contents with a .sneaky-element-inner div.
      return this.getElementHeight(this.$element.children()[0]);
    }

    // Determine the offset from the top of the page at which the sneaky
    // element should start to come into view.
    getScrollBreakpoint() {
      var breakpoint = Number(this.topBuffer);
      if (this.relativeElementId) {
        var relativeElement =
            $document[0].getElementById(this.relativeElementId);
        if (relativeElement) {
          // We just want to add the height of the element to the distance it
          // is from the top of the page. Sadly, angular's `offsetTop` isn't
          // the same as jQuery's, and returns the offset from the containing
          // element. We need the offset from the top of the page. So we have
          // to add `scrollY` to the distance from the top of the page.
          //
          // See http://stackoverflow.com/a/18953277
          breakpoint += relativeElement.getBoundingClientRect().top +
              this.getCurrentScroll() +
              this.getElementHeight(relativeElement);
        }
      }
      return breakpoint;
    }

    updateSneakyElement() {
      if (this.mode === 'height') {
        this.setSneakyElementHeight();
      } else if (this.mode === 'fade-in' || this.mode === 'fade-out') {
        this.setSneakyElementOpacity();
      }
    }

    setSneakyElementHeight() {
      let unboundedSneakyElementHeight =
          this.getCurrentScroll() - this.scrollBreakpoint;
      let innerHeight = this.innerHeight;
      let sneakyElementHeight = Math.max(0,
          Math.min(unboundedSneakyElementHeight, innerHeight));

      if (this.stayOpen) {
        sneakyElementHeight = max([sneakyElementHeight, this.currentHeight]);
      }

      if (sneakyElementHeight !== this.currentHeight) {
        this.$element.css('height', `${sneakyElementHeight}px`);
        this.currentHeight = sneakyElementHeight;
      }
    }

    setSneakyElementOpacity() {
      let isPastBreakpoint = this.getCurrentScroll() > this.scrollBreakpoint;
      let isVisible = (this.mode === 'fade-in' && isPastBreakpoint) ||
        (this.mode === 'fade-out' && !isPastBreakpoint);

      this.$element.css('opacity', isVisible ? 1 : 0);
    }
  }

  return {
    restrict: 'EA',
    transclude: true,
    template: require('./od-sneaky-element.jade')(),
    controller: OdSneakyElement,
    bindToController: true,
    controllerAs: 'sneakyElementCtrl',
    scope: {
      relativeElementId: '@?',
      scrollParentId: '@?',
      topBuffer: '@?',
      mode: '@?',
      stayOpen: '<?',
    }
  };
}).name;



/** WEBPACK FOOTER **
 ** ./javascripts/common/directives/od-sneaky-element.js
 **/